import Immutable from 'immutable';
import React from 'react';

import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import moment, { unitOfTime } from 'moment';

import Button from 'sharedreact/components/Button';
import DropdownInput from 'sharedreact/components/DropdownInput';
import Spinner from 'sharedreact/components/Spinner';
import Tab from 'sharedreact/components/Tab';
import Toolbar from 'sharedreact/components/Toolbar';
import ToolbarGroup from 'sharedreact/components/ToolbarGroup';
import ToolbarTitle from 'sharedreact/components/ToolbarTitle';
import { smartTryToTranslate, translate } from 'sharedreact/utils/LanguageSupport';

type Props = {
  index: number,
  activeTab: number,
  language?: string,
  devSettingsEnabled?: boolean,
  isFetchingQueueData?: boolean,
  isFetchingUserData?: boolean,
  userData?: Immutable.List<any>,
  userKeys?: Immutable.List<string>,
  queueData?: Immutable.List<any>,
  queueKeys?: Immutable.List<string>,
  ranges: Immutable.List<string>,
  currentRange?: string,
  currentViewport?: Immutable.List<number>,

  onLoad: (maxSamples: number) => void,
  onLeave: () => void,
  onZoomIn?: () => void,
  onZoomOut?: () => void,
  onGoBackward?: () => void,
  onGoForward?: () => void,
  onGoFastBackward?: () => void,
  onGoFastForward?: () => void,
  onSetRange: (range: string) => void
};

class LoadHistoryTab extends React.Component<Props> {
  constructor (props: Props) {
    super(props);

    this.handleChangeRangeValue = this.handleChangeRangeValue.bind(this);
    this.renderTimeTick = this.renderTimeTick.bind(this);
  }

  _getGraphWidth(): number {
    let width = parseInt($("#webappslauncher-graph-container").css('width'));
    return width;
  }

  override componentDidMount (): void {
    if (this.props.index === this.props.activeTab) {
      if (this.props.isFetchingQueueData || this.props.isFetchingUserData) {
        return;
      }

      if ((this.props.userData && this.props.userData.size > 0) || (this.props.queueData && this.props.queueData.size > 0)) {
        return
      };
      const maxSamples: number = this._getGraphWidth();
      setTimeout(() => this.props.onLoad(maxSamples), 0);
    }
  }

  override componentDidUpdate (prevProps: Readonly<Props>): void {
    if ((this.props.activeTab !== prevProps.activeTab || this.props.index !== prevProps.index) &&
    this.props.index === this.props.activeTab) {
      if (this.props.isFetchingQueueData || this.props.isFetchingUserData) {
        return
      };

      if ((this.props.userData && this.props.userData.size > 0) || (this.props.queueData && this.props.queueData.size > 0)) {
        return
      };

      const maxSamples: number = this._getGraphWidth();
      setTimeout(() => this.props.onLoad(maxSamples), 0);
    }
  }

  override componentWillUnmount (): void {
    setTimeout(() => this.props.onLeave(), 0);
  }

  handleChangeRangeValue (
    name: string,
    value: any
  ): void {
    this.props.onSetRange(value);
  }

  renderCurrentViewport () {
    const { currentViewport } = this.props;

    if (currentViewport && currentViewport.get(0) && currentViewport.get(1)) {
      return (
        <div className='webappslauncher-loadhistory-viewport'>
          {moment.unix(currentViewport.get(0)! / 1000).format('llll')} - {moment.unix(currentViewport.get(1)! / 1000).format('llll')}
        </div>
      );
    }
    return null;
  }

  renderRangeSelector () {
    const {
      ranges, currentRange } = this.props;

    const placeholder: string = 'launcher.admin.loadhistory.range.none';

    return (
      <div className='webappslauncher-loadhistory-range'>
        <div className='webappslauncher-loadhistory-range-label'>
          <label htmlFor='ae_input_loadhistory_range'>
            {translate('launcher.admin.loadhistory.selectrange')}
          </label>
        </div>
        <DropdownInput<string>
          id='ae_input_loadhistory_range'
          name='loadhistoryRange'
          placeholder={translate(placeholder)}
          value={currentRange ?? null}
          options={ranges}
          disabled={ranges.size == 0}
          onChangeValue={this.handleChangeRangeValue}
          formatOption={(value: string) => {
            if (value === '') { return translate('launcher.admin.loadhistory.range.none'); }
            return translate('launcher.admin.' + value);
          }}
        />
      </div>
    );
  }

  renderTimeTick (unixTime: number): string {
    const { currentViewport } = this.props;
    if (currentViewport && currentViewport.get(0) && currentViewport.get(1)) {
      if (currentViewport.get(1)! - currentViewport.get(0)! < 2 * 24 * 60 * 60 * 1000) {
        // range less than 2 days, show hours:minutes:secs
        return moment(unixTime).format('HH:mm');
      }
    }
    return moment(unixTime).format('D-MMM-YY');
  }

  calculateTicks () {
    const { currentViewport } = this.props;

    let start: moment.Moment;
    let step: unitOfTime.DurationConstructor;
    let stepVal: number = 1;

    if (currentViewport && currentViewport.get(0) && currentViewport.get(1)) {
      const startView: moment.Moment = moment(currentViewport.get(0));
      const endViewport: moment.Moment = moment(currentViewport.get(1));

      if (startView.add(2, 'hours').diff(endViewport) > 0) {
        // less than 2 hours - every 10 minutes
        const startPoint = moment(currentViewport.get(0)).endOf('minute');
        // round to multiple of 10'
        const remainder = (startPoint.minute() % 10);
        if (remainder > 0) {
          start = startPoint.add(10 - remainder, 'minutes');
        } else {
          start = startPoint;
        }
        step = 'minutes';
        stepVal = 10;
      } else if (startView.add(2, 'days').diff(endViewport) > 0) {
        // less than 2 days = every hour
        start = moment(currentViewport.get(0)).startOf('hour').add(1, 'hours');
        step = 'hours';
      } else if (startView.add(20, 'days').diff(endViewport) > 0) {
          // less than 20 days = every day
        start = moment(currentViewport.get(0)).startOf('day').add(1, 'days');
        step = 'days';
      } else if (startView.add(60, 'days').diff(endViewport) > 0) {
        // less then 60 days - every week
        start = moment(currentViewport.get(0)).startOf('week').add(1, 'weeks');
        step = 'weeks';
      } else if (startView.add(20, 'months').diff(endViewport) > 0) {
        start = moment(currentViewport.get(0)).startOf('month').add(1, 'months');
        step = 'months';
      } else if (startView.add(60, 'months').diff(endViewport) > 0) {
        start = moment(currentViewport.get(0)).startOf('month').add(1, 'months');
        step = 'months';
        stepVal = 3;
      } else {
        start = moment(currentViewport.get(0)).startOf('month').add(1, 'months');
        step = 'months';
        stepVal = 6;
      }

      const ticks = [];
      do {
        ticks.push(start.valueOf());

        start.add(stepVal, step);
      } while (start.valueOf() <= currentViewport.get(1)!);
      return ticks;
    }

    return null;
  }

  renderToolbar () {
    const {
       onZoomIn, onZoomOut, onGoBackward, onGoForward, onGoFastBackward, onGoFastForward, devSettingsEnabled,
       isFetchingUserData, isFetchingQueueData,
       language // eslint-disable-line no-unused-vars
    } = this.props;

    return (
      <Toolbar>
        <ToolbarGroup>
          <ToolbarTitle title={translate('launcher.admin.loadhistory.title')} />
          {(isFetchingUserData || isFetchingQueueData)
            ? <Spinner small />
            : null
          }
        </ToolbarGroup>
        <ToolbarGroup align='right'>
          {this.renderRangeSelector()}
        </ToolbarGroup>
        <ToolbarGroup align='right'>
          <Button id='ae_button_loadhistory_fast-backward'
            icon='bi bi-skip-backward-fill'
            title={translate('launcher.admin.loadhistory.fastbackward.tooltip')}
            disabled={onGoFastBackward == null}
            onClick={onGoFastBackward}
          />
          <Button id='ae_button_loadhistory_backward'
            icon='bi bi-rewind-fill'
            title={translate('launcher.admin.loadhistory.backward.tooltip')}
            disabled={onGoBackward == null}
            onClick={onGoBackward}
          />
          <Button id='ae_button_loadhistory_forward'
            icon='bi bi-fast-forward-fill'
            title={translate('launcher.admin.loadhistory.forward.tooltip')}
            disabled={onGoForward == null}
            onClick={onGoForward}
          />
          <Button id='ae_button_loadhistory_fast-forward'
            icon='bi bi-skip-forward-fill'
            title={translate('launcher.admin.loadhistory.fastforward.tooltip')}
            disabled={onGoFastForward == null}
            onClick={onGoFastForward}
          />
          {devSettingsEnabled ? <Button id='ae_button_loadhistory_zoomout'
            icon='bi bi-dash'
            title={translate('launcher.admin.loadhistory.zoomout.tooltip')}
            disabled={onZoomOut == null}
            onClick={onZoomOut}
          /> : null }
          {devSettingsEnabled ? <Button id='ae_button_loadhistory_zoomin'
            icon='bi bi-plus-lg'
            title={translate('launcher.admin.loadhistory.zoomin.tooltip')}
            disabled={onZoomIn == null}
            onClick={onZoomIn}
          /> : null }
        </ToolbarGroup>
      </Toolbar>);
  }

  override render (): React.ReactNode {
    const {
      index, activeTab, userData, queueData, isFetchingUserData, isFetchingQueueData, userKeys, queueKeys,
      devSettingsEnabled
    } = this.props;

    // colors taken from dummy colors for stations in Plato
    const lineColors = ['#4172e7', '#41bd41', '#41bde7', '#e79141', '#914183', '#b4bd41', '#cc99c0',
      '#f5adad', '#f5d7ad', '#8884d8', '#82ca9d', '#67ad1c', '#11f9f7', '#f6270f', '#f911da'];
    const userGraphs = ['total', 'pilot', 'browser', 'other'];

    let userLines: any[] = [];
    let userLines2: any[] = [];
    if (userKeys) {
      for (let i = 0; i < userKeys.size; i++) {
        (userGraphs.includes(userKeys.get(i)!) ? userLines : userLines2).push(
          <Line
            key={'line' + userKeys.get(i)}
            dataKey={userKeys.get(i)}
            name={smartTryToTranslate(userKeys.get(i)!, 'launcher.admin.loadhistory.graph.yaxis.')}
            dot={false}
            type={'linear'}
            stroke={lineColors[Math.min(lineColors.length, (userGraphs.includes(userKeys.get(i)!) ? userLines : userLines2).length)]}
          />
        );
      }
    }

    let queueLines: any[] = [];
    if (queueKeys) {
      for (let ii = 0; ii < queueKeys.size; ii++) {
        if (queueKeys.get(ii)!.toLowerCase() !== 'cpu') {
          if (!devSettingsEnabled) continue;          // we want by default only the cpu queue
          if (queueKeys.get(ii) === 'TASKS') continue;          // we want tasks in a separate graph
        }
        queueLines.push(
          <Line
            key={'line' + queueKeys.get(ii)}
            dataKey={queueKeys.get(ii)}
            name={queueKeys.get(ii)}
            dot={false}
            type={'linear'}
            stroke={lineColors[Math.min(lineColors.length, queueLines.length)]}
        />
        );
      }
    }

    type CustomTooltipProps = {
      active?: boolean,
      payload?: any,
      label?: any
    };

    const CustomTooltip = (props: CustomTooltipProps) => {
      const { active, payload } = props;

      if (active && payload && payload.length) {
        return (
          <div key='loadhistory-tooltip' className='webappslauncher-loadhistory-tooltip'>
            <div className='webappslauncher-loadhistory-tooltip-time' key={'time' + payload[0].payload.time}>{moment.unix(payload[0].payload.time / 1000).format('llll')}</div>

            <table>
              <tbody>
                {payload.map((pld: any) => (
                  <tr key={'tooltiptext' + payload[0].payload.time + pld.dataKey} style={{ color: pld.color }}>
                    <td className='webappslauncher-loadhistory-tooltip-key'>
                      {smartTryToTranslate(pld.dataKey, 'launcher.admin.loadhistory.graph.yaxis.')}:
                  </td><td className='webappslauncher-loadhistory-tooltip-value'>{pld.value}</td>
                  </tr>
          ))}
              </tbody>
            </table>
          </div>
        );
      }
      return null;
    };

    if (index === activeTab) {
      const graphHeight = devSettingsEnabled ? '23%' : '31%';
      const isFetchingMsg = 'common.general.isfetching';
      const xAxisTime = 'launcher.admin.loadhistory.graph.xaxis.time';

      return (
        <Tab index={index} activeIndex={activeTab}>
          {this.renderToolbar()}
          <div className='webappslauncher-scroll-container'>
            {this.renderCurrentViewport()}
            <ResponsiveContainer width='90%' height={graphHeight} minWidth={200} minHeight={200} id='webappslauncher-graph-container'>
              {(isFetchingQueueData && (!queueData || queueData.size == 0))
                ? <Spinner label={translate(isFetchingMsg)} />
                : <LineChart data={queueData ? queueData.toArray() : undefined} margin={{ top: 20, right: 10, left: 40, bottom: 10 }}>
                  <CartesianGrid />
                  <XAxis type='number' dataKey='time' domain={['dataMin', 'dataMax']} ticks={this.calculateTicks() ?? undefined} tickFormatter={this.renderTimeTick}>
                    <Label
                      value={translate(xAxisTime)}
                      position='insideBottomRight'
                      offset={-10}
                      style={{ textAnchor: 'end' }}
                    />
                  </XAxis>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <YAxis allowDecimals={false}>
                    <Label
                      value={translate('launcher.admin.loadhistory.graph.yaxis.queue')}
                      position='left'
                      angle={-90}
                      style={{ textAnchor: 'middle' }}
                    />
                  </YAxis>
                  {queueLines}
                </LineChart>
              }
            </ResponsiveContainer>
            <ResponsiveContainer width='90%' height={graphHeight} minWidth={200} minHeight={200}>
              {(isFetchingUserData && (!userData || userData.size == 0))
                ? <Spinner label={translate(isFetchingMsg)} />
                : <LineChart data={userData ? userData.toArray() : undefined} margin={{ top: 20, right: 10, left: 40, bottom: 10 }}>
                  <CartesianGrid />
                  <XAxis type='number' dataKey='time' domain={['dataMin', 'dataMax']} ticks={this.calculateTicks() ?? undefined} tickFormatter={this.renderTimeTick}>
                    <Label
                      value={translate(xAxisTime)}
                      position='insideBottomRight'
                      offset={-10}
                      style={{ textAnchor: 'end' }}
                    />
                  </XAxis>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <YAxis allowDecimals={false}>
                    <Label
                      value={translate('launcher.admin.loadhistory.graph.yaxis.user1')}
                      position='left'
                      angle={-90}
                      style={{ textAnchor: 'middle' }}
                    />
                  </YAxis>
                  {userLines}
                </LineChart>
              }
            </ResponsiveContainer>
            <ResponsiveContainer width='90%' height={graphHeight} minWidth={200} minHeight={200}>
              {(isFetchingUserData && (!userData || userData.size == 0))
                ? <Spinner label={translate(isFetchingMsg)} />
                : <LineChart data={userData ? userData.toArray() : undefined} margin={{ top: 20, right: 10, left: 40, bottom: 10 }}>
                  <CartesianGrid />
                  <XAxis type='number' dataKey='time' domain={['dataMin', 'dataMax']} ticks={this.calculateTicks() ?? undefined} tickFormatter={this.renderTimeTick}>
                    <Label
                      value={translate(xAxisTime)}
                      position='insideBottomRight'
                      offset={-10}
                      style={{ textAnchor: 'end' }}
                    />
                  </XAxis>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <YAxis allowDecimals={false}>
                    <Label
                      value={translate('launcher.admin.loadhistory.graph.yaxis.user2')}
                      position='left'
                      angle={-90}
                      style={{ textAnchor: 'middle' }}
                    />
                  </YAxis>
                  {userLines2}
                </LineChart>
              }
            </ResponsiveContainer>
            {devSettingsEnabled
            ? <ResponsiveContainer width='90%' height={graphHeight} minWidth={200} minHeight={200}>
              {isFetchingQueueData
                ? <Spinner label={translate(isFetchingMsg)} />
                : <LineChart data={queueData ? queueData.toArray() : undefined} margin={{ top: 20, right: 10, left: 40, bottom: 10 }}>
                  <CartesianGrid />
                  <XAxis type='number' dataKey='time' domain={['dataMin', 'dataMax']} ticks={this.calculateTicks() ?? undefined} tickFormatter={this.renderTimeTick}>
                    <Label
                      value={translate(xAxisTime)}
                      position='insideBottomRight'
                      offset={-10}
                      style={{ textAnchor: 'end' }}
                    />
                  </XAxis>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <YAxis allowDecimals={false}>
                    <Label
                      value={translate('launcher.admin.loadhistory.graph.yaxis.queue')}
                      position='left'
                      angle={-90}
                      style={{ textAnchor: 'middle' }}
                    />
                  </YAxis>
                  <Line
                    key='line TASKS'
                    dataKey='TASKS'
                    name={translate('launcher.admin.loadhistory.graph.yaxis.tasks')}
                    dot={false}
                    type={'linear'}
                    stroke={lineColors[0]}
                  />
                </LineChart>
              }
            </ResponsiveContainer> : null }
          </div>
        </Tab>
      );
    } else {
      return null;
    }
  }
}

export default LoadHistoryTab;
